import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "../data";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { whatsappLink } from "../whatsapp";
import { saveMessage } from "../firebase";

const HOURS = [
  { day: "Monday – Saturday", hours: "8 am – 7 pm" },
  { day: "Sunday", hours: "9 am – 12 pm" },
];

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const name = data.get("name");
    const message = data.get("message");
    const phone = data.get("phone");
    const email = data.get("email");

    setSubmitting(true);
    try {
      await saveMessage({
        name,
        phone,
        email: email || null,
        message,
      });
      e.target.reset();
      toast.success(`Thanks ${name}, we got your note.`, {
        description: "We'll reply within a day. Quicker on WhatsApp.",
      });
      const text = `Hi Organo Mushroom 👋\n\nFrom: ${name}\nPhone: ${phone}\n\n${message}`;
      window.open(whatsappLink(text), "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't save your message", {
        description: "Please WhatsApp us directly — we'll respond fast.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="relative">
      <span className="stamp-num absolute top-6 right-6 md:right-12">N° 07</span>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-12">
        <p className="section-num">
          <strong>07</strong> Get in touch
        </p>
        <div className="grid md:grid-cols-12 gap-10 items-end mt-5">
          <h1 className="md:col-span-8 font-display text-display-xl text-ink tracking-tightest fade-up">
            Say hello,
            <br />
            <span className="font-italic text-coral">we reply quickly.</span>
          </h1>
          <p className="md:col-span-4 text-ink/65 max-w-md text-[15px] leading-relaxed fade-up-d1">
            For orders, workshop bookings, bulk enquiries, or just to chat
            about mushrooms. WhatsApp is fastest, email is fine, walk-ins are
            welcome.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 grid md:grid-cols-12 gap-12">
        {/* INFO BLOCKS */}
        <aside className="md:col-span-5 space-y-5">
          {/* Address card */}
          <div className="rounded-4xl bg-sageFog p-7 md:p-8 relative overflow-hidden">
            <span className="absolute -top-6 -right-6 h-32 w-32 rounded-full bg-sage/30 blur-2xl" aria-hidden />
            <p className="section-num"><strong>·</strong> Visit the unit</p>
            <h3 className="font-display text-[28px] mt-4 text-ink leading-tight tracking-tightest">
              <MapPin className="inline h-5 w-5 text-coral mr-2 -mt-1" />
              Organo Mushroom
            </h3>
            <p className="mt-3 text-ink/75 leading-relaxed text-[15px]">{CONTACT.address}</p>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Organo+Mushroom+Manish+Nagar+Nagpur"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-map-link"
              className="link-underline inline-flex items-center gap-2 mt-5 text-sm font-medium text-coral"
            >
              Open in Google Maps
            </a>
          </div>

          {/* Phone / Email */}
          <div className="rounded-4xl bg-boneSoft p-7 md:p-8 border border-ink/5">
            <p className="section-num"><strong>·</strong> Direct line</p>
            <ul className="mt-5 space-y-4">
              {CONTACT.phones.map((p) => (
                <li key={p}>
                  <a
                    data-testid={`contact-phone-${p.replace(/\s/g, "")}`}
                    href={`tel:+91${p.replace(/\s/g, "")}`}
                    className="link-underline inline-flex items-center gap-3 text-ink hover:text-coral transition-colors text-[16px]"
                  >
                    <Phone className="h-4 w-4 text-coral" /> +91 {p}
                  </a>
                </li>
              ))}
              <li>
                <a
                  data-testid="contact-email"
                  href={`mailto:${CONTACT.email}`}
                  className="link-underline inline-flex items-center gap-3 text-ink hover:text-coral transition-colors text-[16px]"
                >
                  <Mail className="h-4 w-4 text-coral" /> {CONTACT.email}
                </a>
              </li>
              <li>
                <a
                  data-testid="contact-whatsapp"
                  href={whatsappLink("Hi Organo Mushroom 👋\n\nI'd like to place an order / ask a question.")}
                  target="_blank"
                  rel="noreferrer"
                  className="link-underline inline-flex items-center gap-3 text-ink hover:text-whatsapp transition-colors text-[16px]"
                >
                  <WhatsAppIcon className="h-4 w-4 text-whatsapp" /> Chat on WhatsApp
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div className="rounded-4xl bg-boneSoft p-7 md:p-8 border border-ink/5">
            <p className="section-num"><strong>·</strong> Opening hours</p>
            <ul className="mt-5 divide-y divide-ink/10">
              {HOURS.map((h) => (
                <li key={h.day} className="flex items-center justify-between py-3">
                  <span className="text-ink/80 text-[15px] inline-flex items-center gap-2.5">
                    <Clock className="h-3.5 w-3.5 text-coral" /> {h.day}
                  </span>
                  <span className="font-display text-lg text-ink tabular-nums">{h.hours}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* CONTACT FORM */}
        <div className="md:col-span-7 relative">
          <span className="stamp-num absolute -top-6 right-2 text-ink/[0.04]">Hello</span>
          <div className="relative bg-ink text-bone rounded-4xl p-8 md:p-12 shadow-hoverCard">
            <p className="section-num text-bone/45">
              <strong className="text-butter">·</strong> Write to us
            </p>
            <h2 className="font-display text-display-md mt-4 tracking-tightest">
              Drop us a note,
              <br />
              <span className="font-italic text-butter">we'll WhatsApp you back.</span>
            </h2>

            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="mt-10 space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Name" name="name" required testid="contact-name" />
                <Field label="Phone" name="phone" type="tel" required testid="contact-phone-input" />
              </div>
              <Field label="Email (optional)" name="email" type="email" testid="contact-email-input" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.28em] text-bone/55 block mb-2">
                  Your message
                </label>
                <textarea
                  name="message"
                  data-testid="contact-message"
                  required
                  rows={4}
                  placeholder="Tell us what you're looking for…"
                  className="w-full bg-transparent border-b border-bone/25 focus:border-butter outline-none py-3 text-bone placeholder:text-bone/35 transition-colors resize-none"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={submitting}
                className="btn-primary bg-butter text-ink hover:bg-bone disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send message"}
                <span className="knob bg-ink text-butter">
                  <Send className="h-4 w-4" />
                </span>
              </button>
              <p className="text-xs text-bone/45 mt-3">
                Submitting will also open WhatsApp with your message ready to send.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.28em] text-bone/55 block mb-2">
        {label}{required && <span className="text-butter ml-0.5">*</span>}
      </label>
      <input
        data-testid={testid}
        name={name}
        type={type}
        required={required}
        className="w-full bg-transparent border-b border-bone/25 focus:border-butter outline-none py-3 text-bone placeholder:text-bone/35 transition-colors"
      />
    </div>
  );
}
